import { t } from "@lingui/macro";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { LinearGradient } from "expo-linear-gradient";
import { useFragment } from "react-relay";

import {
  VStack,
  Text,
  Box,
} from "@whoosh/design-system/src/components/primitive";
import { theme } from "@whoosh/design-system/src/theme/restyle/theme";

import { useFlags } from "../../../../../../../utils/launchDarkly";
import { getBookingPlayerNoun } from "../../utils/getBookingPlayerNoun";
import { useShouldPresentBookingAsLockedRequest } from "../BookingRequest/components/AvailableTeeTimes/hooks/useShouldPresentBookingAsLockedRequest";
import {
  ChargePaymentStatus,
  InviteOthersBox_Booking$key,
} from "./__generated__/InviteOthersBox_Booking.graphql";
import { IconCardStack } from "./components/IconCardStack/IconCardStack";
import { InviteActions } from "./components/InviteActions/InviteActions";

const acceptableChargeStates: ChargePaymentStatus[] = ["UNPAID", "NO_CHARGES"];

type Props = {
  bookingRef: InviteOthersBox_Booking$key | null;
  clubSlug?: string | null;
};
export const InviteOthersBox = ({ bookingRef, clubSlug }: Props) => {
  const { patronJoinBookingFlow, inviteMemberFlow } = useFlags();

  const booking = useFragment(InviteOthersBoxFragmentString, bookingRef);
  const shouldPresentAsRequest = useShouldPresentBookingAsLockedRequest(
    booking ?? null
  );
  const isConfirmedBooking = booking?.status === "CONFIRMED";
  const clubSupportsPrivateBookings =
    booking?.startSlot?.agenda?.facility?.club?.supportsPrivate;
  const hasUnknownPlayers = booking?.rounds?.edges?.some(
    (edge) => !edge?.node?.player?.id
  );
  const hasMemberHoldSlots = booking?.rounds?.edges?.some(
    (edge) =>
      edge?.node?.__typename === "MemberRound" && !edge?.node?.player?.id
  );
  const requiresPlayerCounts = booking?.startSlot?.requirePlayerCounts;

  const isDurationFacility =
    booking?.startSlot?.agenda?.facility?.type !== "GOLF_COURSE";

  const hasAvailableCapacity = (booking?.startSlot?.availableCapacity ?? 0) > 0;
  const isNotYetPaid =
    !!acceptableChargeStates.includes(booking?.paymentStatus ?? "PAID") &&
    !!booking?.rounds?.edges?.every(
      (edge) =>
        edge?.node?.__typename === "CustomerRound" &&
        acceptableChargeStates.includes(edge?.node?.paymentStatus ?? "PAID")
    );

  /**
   * For duration facilities -- when the booking is unpaid -- we allow them to "join" as long
   * as capacity exists, even if there aren't underlying 'unknown players'.
   *
   * For golf -- or for paid bookings -- unknown player rounds must exist to join.
   */

  const hasSlotsToInvite =
    hasUnknownPlayers ||
    (hasAvailableCapacity &&
      isNotYetPaid &&
      isDurationFacility &&
      !requiresPlayerCounts);

  const isMobile = useResponsiveProp({ base: 1, md: 0 });

  /**
   * We only render the invite box if we are a confirmed booking AND we
   * have slots to invite.
   * We don't show invite box for lesson bookings.
   */
  const shouldShowPublicInvite =
    patronJoinBookingFlow &&
    isConfirmedBooking &&
    hasSlotsToInvite &&
    !clubSupportsPrivateBookings;

  const shouldShowPrivateInvite =
    inviteMemberFlow &&
    isConfirmedBooking &&
    hasMemberHoldSlots &&
    !!clubSupportsPrivateBookings;

  const shouldShowInviteBox =
    booking?.__typename !== "LessonBooking" &&
    !shouldPresentAsRequest &&
    (shouldShowPublicInvite || shouldShowPrivateInvite);
  const playerNoun = getBookingPlayerNoun({
    bookingType: booking?.__typename,
    copyOverrides: booking?.startSlot?.agenda?.facility?.copyOverrides,
    lowercaseFirstLetter: true,
  });
  const inviteDescription = clubSupportsPrivateBookings
    ? t`Share this booking with other members to ensure everyone stays informed and nothing important is missed.`
    : t`Share the booking with other ${playerNoun.plural} to ensure everyone stays informed and nothing important is missed.`;

  return shouldShowInviteBox ? (
    <VStack
      backgroundColor="surface-primary"
      position="relative"
      flexDirection={isMobile ? "column" : "row"}
      testID="invite-others-box"
      paddingHorizontal={isMobile ? 0 : 5}
      paddingBottom={isMobile ? 5 : 0}
    >
      <Box height={isMobile ? 80 : 114} width={isMobile ? "100%" : 208}>
        <LinearGradient
          colors={[
            theme.colors["surface-main-primary-subtlest"],
            theme.colors["surface-main-primary-subtler"],
          ]}
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          style={{
            width: "100%",
            height: isMobile ? 80 : 114,
            borderRadius: isMobile ? 0 : 12,
          }}
        />

        <IconCardStack isMobile={!!isMobile} />
      </Box>
      <VStack paddingHorizontal={5} paddingTop={isMobile ? 5 : 0} flex={1}>
        <Text
          textAlign={isMobile ? "center" : "left"}
          variant={isMobile ? "mainHeading" : "mainBaseSemiBold"}
        >
          {t`Invite others to this booking`}
        </Text>
        <Box
          maxWidth={isMobile ? 353 : "100%"}
          marginLeft="auto"
          marginRight="auto"
          paddingTop={2}
          paddingBottom={isMobile ? 2 : 0}
        >
          <Text
            textAlign={isMobile ? "center" : "left"}
            variant="mainSmallMedium"
            color="text-secondary"
          >
            {inviteDescription}
          </Text>
        </Box>
        <InviteActions clubSlug={clubSlug ?? ""} bookingRef={booking} />
      </VStack>
    </VStack>
  ) : null;
};

const InviteOthersBoxFragmentString = graphql`
  fragment InviteOthersBox_Booking on Booking {
    startSlot {
      ... on TimeSlot {
        availableCapacity(bookingType: STANDARD)
        requirePlayerCounts
      }
      agenda {
        facility {
          type
          copyOverrides {
            bookingPlayer {
              plural
            }
          }
          club {
            supportsPrivate
          }
        }
      }
    }
    __typename
    id
    ...useShouldPresentBookingAsLockedRequest_booking
    ...InviteActions_Booking
    ... on StandardBooking {
      status
      paymentStatus
      rounds {
        edges {
          node {
            __typename
            player {
              id
            }
            ... on CustomerRound {
              id
              paymentStatus
            }
          }
        }
      }
    }
  }
`;
